import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import Card from './Card';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import type { StyleProp, ViewStyle } from 'react-native';

/**
 * CollapsibleSection - a Card whose body hides behind a tappable header
 * (title + subtitle + chevron). Used on the pay screen to condense the
 * membership/bundle upsells without moving them. Collapsed by default;
 * no LayoutAnimation (works identically on web and native).
 */
interface CollapsibleSectionProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: keyof typeof Ionicons.glyphMap;
  readonly iconColor?: string;
  readonly initiallyExpanded?: boolean;
  readonly style?: StyleProp<ViewStyle>;
  readonly children: React.ReactNode;
}

export default function CollapsibleSection({
  title, subtitle, icon, iconColor, initiallyExpanded = false, style, children,
}: CollapsibleSectionProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [expanded, setExpanded] = useState(initiallyExpanded);

  return (
    <Card style={style}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setExpanded(e => !e)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={title}
        accessibilityHint={expanded ? 'Collapses this section' : 'Expands this section'}
      >
        {icon && <Ionicons name={icon} size={20} color={iconColor || colors.primary} />}
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>{title}</Text>
          {subtitle && !expanded && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={20} color={colors.textSecondary} />
      </TouchableOpacity>
      {expanded && <View style={styles.body}>{children}</View>}
    </Card>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  title: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  subtitle: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  body: { marginTop: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
